import {
  type UseQueryResult,
  type UseSuspenseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import type { Column, ColumnDef } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, Eye, Pencil, SearchX, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Cell,
  DataGrid,
  GridCaption,
  GridHeader,
  GridRow,
  HeaderCell,
  RowAction,
  RowActions,
  RowCheckbox,
} from '@/components/ui/admin/data-grid';
import { GridPagination } from '@/components/ui/admin/grid-pagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Spinner } from './spinner';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    /** Fixa a coluna nessa borda da tabela, sempre visível ao rolar horizontalmente. */
    sticky?: 'left' | 'right';
    /** Rótulo legível da coluna — usado em "Ordenado por" (o `id` da coluna é o nome técnico). */
    label?: string;
    /** Tipo original da coluna na screen (avatar, email, phone, ...) — usado pro layout do grid. */
    type?: string;
  }
}

const STICKY_COLUMN_CLASSNAME: Record<'left' | 'right', string> = {
  left: 'sticky left-0 z-10 bg-card',
  right: 'sticky right-0 z-10 bg-card',
};

type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

type AnyFetcher = (
  ...args: any[]
) => UseSuspenseQueryResult<any, any> | UseQueryResult<any, any>;

export type InferFetcherParams<TFetcher extends AnyFetcher> = TFetcher extends (
  params?: infer P,
) => any
  ? NonNullable<P>
  : never;

export type InferFetcherResponse<TFetcher extends AnyFetcher> = NonNullable<
  ReturnType<TFetcher>['data']
>;

export type InferRow<TFetcher extends AnyFetcher> =
  InferFetcherResponse<TFetcher> extends {
    data: (infer Row)[];
  }
    ? Prettify<Row>
    : never;

interface DataTableBaseProps<TRow> {
  columns: ColumnDef<TRow>[];
  pageSize?: number;
  onRowDoubleClick?: (row: TRow) => void;
  onRowClick?: (row: TRow) => void;
  defaultSort?: {
    id: keyof TRow;
    desc: boolean;
  };
  /** Adiciona a coluna de checkbox e habilita seleção em massa. */
  enableSelection?: boolean;
  /** Chamado com os ids selecionados ao confirmar "Excluir selecionados". */
  onBulkRemove?: (ids: string[]) => Promise<void> | void;
}

export type DataTableRemoteProps<
  TFetcher extends AnyFetcher,
  TRow = InferRow<TFetcher>,
> = Prettify<
  DataTableBaseProps<TRow> & {
    useFetcher: TFetcher;
    search?: Partial<InferFetcherParams<TFetcher>>;
    data?: never;
  }
>;

export type DataTableLocalProps<TRow> = Prettify<
  DataTableBaseProps<TRow> & {
    data: TRow[];
    useFetcher?: never;
    search?: never;
  }
>;

export type DataTableProps<TFetcher extends AnyFetcher, TRow = InferRow<TFetcher>> =
  | DataTableRemoteProps<TFetcher, TRow>
  | DataTableLocalProps<TRow>;

function useStaticFetcher() {
  return { data: undefined, isLoading: false, isFetching: false } as any;
}

export function DataTable<TFetcher extends AnyFetcher = any, TRow = InferRow<TFetcher>>(
  props: DataTableProps<TFetcher, TRow>,
) {
  const {
    columns,
    pageSize = 10,
    onRowDoubleClick,
    onRowClick,
    defaultSort,
    enableSelection,
    onBulkRemove,
  } = props;

  const [isBulkRemoveOpen, setIsBulkRemoveOpen] = useState(false);
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);

  const finalColumns = useMemo<ColumnDef<TRow>[]>(() => {
    if (!enableSelection) return columns;

    const selectColumn: ColumnDef<TRow> = {
      id: 'select',
      enableSorting: false,
      header: ({ table }) => (
        <RowCheckbox
          aria-label="Selecionar todos"
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected()}
          onChange={(checked) => table.toggleAllPageRowsSelected(checked)}
        />
      ),
      cell: ({ row }) => (
        <RowCheckbox
          aria-label="Selecionar linha"
          checked={row.getIsSelected()}
          onChange={(checked) => row.toggleSelected(checked)}
        />
      ),
    };

    return [selectColumn, ...columns];
  }, [columns, enableSelection]);

  const isLocal = 'data' in props && props.data !== undefined;

  const search = !isLocal && 'search' in props ? (props.search ?? {}) : {};

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: (defaultSort?.id as string) ?? 'createdAt',
      desc: defaultSort?.desc ?? true,
    },
  ]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [JSON.stringify(search)]);

  const useActiveFetcher: AnyFetcher = (
    !isLocal && 'useFetcher' in props ? props.useFetcher : useStaticFetcher
  ) as AnyFetcher;

  const {
    data: fetcherData,
    isLoading,
    isFetching,
  } = useActiveFetcher({
    page: pagination.pageIndex + 1,
    perPage: pagination.pageSize,
    orderBy: sorting[0]?.id ?? 'id',
    order: sorting[0]?.desc ? 'desc' : 'asc',
    ...Object.fromEntries(Object.entries(search).filter(([_, v]) => v)),
  } as any);

  const rows = isLocal ? ((props.data as TRow[]) ?? []) : (fetcherData?.data ?? []);

  const table = useReactTable({
    data: rows,
    columns: finalColumns,
    pageCount: isLocal ? undefined : (fetcherData?.pagination?.totalPages ?? 0),
    state: {
      sorting,
      pagination,
      columnVisibility,
      rowSelection,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualSorting: !isLocal,
    manualPagination: !isLocal,
    manualFiltering: !isLocal,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = fetcherData?.pagination?.totalPages ?? 0;
  const totalItems = fetcherData?.pagination?.total ?? 0;
  const currentPage = pagination.pageIndex + 1;

  const gridColumns = table
    .getVisibleFlatColumns()
    .map((column) => {
      if (column.id === 'select') return '44px';
      if (column.id === 'actions') return '116px';
      if (column.id === 'id') return '128px';
      if (column.columnDef.meta?.type === 'avatar') return '64px';
      return 'minmax(160px,1.3fr)';
    })
    .join(' ');

  const sortedColumn = sorting[0];
  const sortedColumnLabel = sortedColumn
    ? (table.getColumn(sortedColumn.id)?.columnDef.meta?.label ?? sortedColumn.id)
    : undefined;

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedIds = selectedRows.map((row) => (row.original as any).id);

  const handleConfirmBulkRemove = async () => {
    if (!onBulkRemove) return;
    setIsBulkRemoving(true);
    try {
      await onBulkRemove(selectedIds);
      setRowSelection({});
    } finally {
      setIsBulkRemoving(false);
      setIsBulkRemoveOpen(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        {isFetching && !isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-[14px]">
            <Spinner />
          </div>
        )}

        <AlertDialog open={isBulkRemoveOpen} onOpenChange={setIsBulkRemoveOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-center">
                Excluir {selectedIds.length}{' '}
                {selectedIds.length === 1 ? 'registro selecionado' : 'registros selecionados'}?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-center">
                Isso não poderá ser desfeito.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <div className="flex w-full gap-4 items-center justify-center">
                <AlertDialogCancel onClick={() => setIsBulkRemoveOpen(false)}>
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirmBulkRemove} disabled={isBulkRemoving}>
                  Confirmar
                </AlertDialogAction>
              </div>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <DataGrid
          columns={gridColumns}
          maxHeight="min(600px, calc(100vh - 380px))"
          caption={
            !isLocal ? (
              selectedIds.length > 0 ? (
                <div className="flex items-center gap-3 border-b border-border px-[22px] py-3">
                  <span className="col-label text-[10.5px] text-olive-ink">
                    {selectedIds.length} selecionado{selectedIds.length > 1 ? 's' : ''}
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setIsBulkRemoveOpen(true)}
                  >
                    <Trash2 className="size-[14px]" /> Excluir selecionados
                  </Button>
                </div>
              ) : (
                <GridCaption
                  trailing={
                    sortedColumn && (
                      <>
                        Ordenado por{' '}
                        <span className="text-olive-ink">
                          {sortedColumnLabel}
                          {sortedColumn.desc ? (
                            <ArrowDown className="ml-1 inline size-3" />
                          ) : (
                            <ArrowUp className="ml-1 inline size-3" />
                          )}
                        </span>
                      </>
                    )
                  }
                >
                  Exibindo {Math.min(pagination.pageSize, rows.length)} de {totalItems} registros
                </GridCaption>
              )
            ) : undefined
          }
          footer={
            !isLocal ? (
              <GridPagination
                page={currentPage}
                pages={totalPages || 1}
                perPage={pagination.pageSize}
                total={totalItems}
                onPage={(p) => table.setPageIndex(p - 1)}
                onPerPage={(n) => table.setPageSize(n)}
              />
            ) : undefined
          }
        >
          {table.getHeaderGroups().map((group) => (
            <GridHeader key={group.id}>
              {group.headers.map((header) => {
                const sticky = header.column.columnDef.meta?.sticky;
                const canSort = header.column.getCanSort?.() && header.column.columnDef.enableSorting !== false;
                const sorted = header.column.getIsSorted();

                return (
                  <HeaderCell
                    key={header.id}
                    align={header.column.id === 'actions' ? 'center' : 'left'}
                    active={!!sorted}
                    className={sticky ? STICKY_COLUMN_CLASSNAME[sticky] : undefined}
                    onClick={
                      canSort ? () => header.column.toggleSorting(sorted === 'asc') : undefined
                    }
                  >
                    <span className="inline-flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sorted &&
                        (sorted === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : (
                          <ArrowDown className="size-3" />
                        ))}
                    </span>
                  </HeaderCell>
                );
              })}
            </GridHeader>
          ))}

          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <GridRow key={i}>
                {table.getVisibleFlatColumns().map((_, j) => (
                  <Cell key={j}>
                    <div className="h-4 w-full rounded bg-muted animate-pulse" />
                  </Cell>
                ))}
              </GridRow>
            ))
          ) : table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <GridRow
                key={row.id}
                onDoubleClick={() => onRowDoubleClick?.(row.original)}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => {
                  const sticky = cell.column.columnDef.meta?.sticky;

                  return (
                    <Cell
                      key={cell.id}
                      align={
                        cell.column.id === 'actions'
                          ? 'right'
                          : cell.column.columnDef.meta?.type === 'avatar'
                            ? 'center'
                            : 'left'
                      }
                      truncate
                      className={sticky ? STICKY_COLUMN_CLASSNAME[sticky] : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Cell>
                  );
                })}
              </GridRow>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-14 text-muted-foreground">
              <SearchX size={28} />
              <span className="text-sm">Nenhum resultado encontrado</span>
            </div>
          )}
        </DataGrid>
      </div>
    </div>
  );
}

interface SortableHeaderProps<TData> {
  column: Column<TData, unknown>;
  label: string;
}

/** Cabeçalho ordenável — o clique/estado vêm de `HeaderCell`; isso só renderiza o texto. */
export function SortableHeader<TData>({ label }: SortableHeaderProps<TData>) {
  return <>{label}</>;
}

interface ActionsButtonProps {
  id?: any;
  edit?: boolean;
  remove?: boolean;
  view?: boolean;
  onEdit?: (id: any) => void;
  onView?: (id: any) => void;
  onRemove?: (id: any) => void;
  queryKey?: any;
  onRemoveSuccess?: () => void;
  /** Descrição customizada pro modal de confirmação de exclusão — chamada ao abrir o modal. Retornando `undefined` mantém o texto padrão. */
  getRemoveDescription?: (id: any) => Promise<string | undefined>;
  children?: React.ReactNode;
}

export const ActionsButton: React.FC<ActionsButtonProps> = ({
  children,
  edit,
  id,
  remove,
  view,
  onEdit,
  onRemove,
  onView,
  onRemoveSuccess,
  queryKey,
  getRemoveDescription,
}) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false);
  const [removeDescription, setRemoveDescription] = useState<string | undefined>(
    undefined,
  );
  const [loadingRemoveDescription, setLoadingRemoveDescription] = useState(false);
  const queryClient = useQueryClient();

  const handleEdit = () => {
    if (onEdit) onEdit(id);
  };

  const handleView = () => {
    if (onView) onView(id);
  };

  const handleConfirmRemove = async () => {
    if (onRemove) {
      await onRemove(id);

      if (queryKey) {
        queryClient.invalidateQueries({
          queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
        });
      }

      if (onRemoveSuccess) {
        onRemoveSuccess();
      }
    }
    setIsRemoveModalOpen(false);
  };

  const handleRemove = () => {
    setIsRemoveModalOpen(true);
    if (getRemoveDescription) {
      setRemoveDescription(undefined);
      setLoadingRemoveDescription(true);
      getRemoveDescription(id)
        .then(setRemoveDescription)
        .catch(() => setRemoveDescription(undefined))
        .finally(() => setLoadingRemoveDescription(false));
    }
  };

  useEffect(() => {
    const closeOnEscapeKeyDown = (event: KeyboardEvent) => {
      if ((event.key === 'Escape' || event.key === 'Esc') && isRemoveModalOpen) {
        setIsRemoveModalOpen(false);
      }
    };

    document.addEventListener('keydown', closeOnEscapeKeyDown);
    return () => document.removeEventListener('keydown', closeOnEscapeKeyDown);
  }, [isRemoveModalOpen]);

  const removeModal = (
    <AlertDialog onOpenChange={setIsRemoveModalOpen} open={isRemoveModalOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center">
            Você tem certeza que deseja excluir esses dados?
          </AlertDialogTitle>
          <AlertDialogDescription className="w-full  py-4flex flex-col items-center justify-center">
            <svg
              className="mx-auto mb-4 text-muted-foreground w-12 h-12"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <h3 className="mb-5 text-lg font-normal text-muted-foreground text-center">
              {loadingRemoveDescription
                ? 'Verificando dados relacionados...'
                : (removeDescription ?? 'Isso não poderá ser desfeito.')}
            </h3>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <div className="flex w-full gap-4 items-center justify-center">
            <AlertDialogCancel onClick={() => setIsRemoveModalOpen(false)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              disabled={loadingRemoveDescription}
            >
              Confirmar
            </AlertDialogAction>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <>
      {removeModal}
      <RowActions>
        {edit ? (
          <RowAction tone="accent" onClick={handleEdit} title="Editar">
            <Pencil className="size-[14px]" />
          </RowAction>
        ) : null}
        {view ? (
          <RowAction tone="neutral" onClick={handleView} title="Visualizar">
            <Eye className="size-[14px]" />
          </RowAction>
        ) : null}
        {remove ? (
          <RowAction tone="danger" onClick={handleRemove} title="Excluir">
            <Trash2 className="size-[14px]" />
          </RowAction>
        ) : null}
        {children}
      </RowActions>
    </>
  );
};
